import { defineNuxtPlugin, useRuntimeConfig } from 'nuxt/app'

import { APP_NAMES, setAppType, type AppType } from '../composables/useGlobal'
import { setResolvedEnvironment } from '../config/resolved-environment'

/**
 * The slice of `runtimeConfig.public` this plugin needs.
 *
 * Both values are set in `libs/config/nuxt.base.config.ts` and are the same source
 * `useEnv()` reads. Neither is read from `window.__NUXT__` any more: Nuxt deletes that
 * global in `nuxt:revive-payload:client` from 4.3.1 onward.
 */
export interface InitAppRuntimeConfig {
  appName?: string
  appEnv?: string
}

/**
 * Resolve the app type from runtime config, falling back to `APP_NAME` in the
 * environment (set by `scripts/dev.js` and the build scripts) and finally to
 * ClientPortal. An unrecognised name is ignored rather than trusted.
 */
export function resolveAppType(publicConfig: InitAppRuntimeConfig): AppType {
  const validAppNames = Object.values(APP_NAMES) as string[]

  const candidates = [publicConfig.appName, typeof process !== 'undefined' ? process.env?.APP_NAME : undefined]

  for (const candidate of candidates) {
    const normalized = candidate?.toUpperCase()
    if (normalized && validAppNames.includes(normalized)) {
      return normalized as AppType
    }
  }

  return APP_NAMES.CLIENTPORTAL
}

/**
 * Publish the app's identity for the rest of the runtime.
 *
 * Exported separately from the plugin so it is testable without a Nuxt app context —
 * the same pattern as `runInitLocation` in `libs/plugins/init-location.ts`.
 */
export function runInitApp(publicConfig: InitAppRuntimeConfig): AppType {
  // config/index.ts cannot read the payload itself — it is a build-time import of every
  // nuxt.config.ts — so the environment is pushed in from here. `resolve-environment.ts`
  // already published it at `enforce: 'pre'`; this repeat is idempotent and keeps the server
  // path and this module's own tests working without depending on plugin order.
  setResolvedEnvironment(publicConfig.appEnv)

  const appType = resolveAppType(publicConfig)
  setAppType(appType)

  return appType
}

// Auto-detect and initialize app type based on current app context
// This replaces per-app init-app.ts files with a unified solution
export default defineNuxtPlugin(() => {
  try {
    // Cast the config itself rather than its `public` property: this repo's `RuntimeConfig`
    // type does not declare `public`, so reaching through it is a type error (TS2339).
    const { public: publicConfig } = useRuntimeConfig() as unknown as { public: InitAppRuntimeConfig }
    runInitApp(publicConfig)
  } catch (error) {
    // The environment is not at risk here — `resolve-environment.ts` publishes it at
    // `enforce: 'pre'`, before this plugin runs. Only the app type needs a fallback.
    console.warn('[Init-App] Failed to read runtime config, defaulting to clientportal:', error)
    setAppType(APP_NAMES.CLIENTPORTAL)
  }
})
